import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for adding auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for handling auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  getMe: () => api.get('/auth/me'),
  forgotPassword: (data) => api.post('/auth/forgot-password', data),
};

// User APIs
export const userAPI = {
  getAll: (params) => api.get('/users', { params }),
  lookup: (params) => api.get('/users/lookup', { params }),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
};

// Lead APIs
export const leadAPI = {
  getAll: (params) => api.get('/leads', { params }),
  getOne: (id) => api.get(`/leads/${id}`),
  create: (data) => api.post('/leads', data),
  update: (id, data) => api.put(`/leads/${id}`, data),
  addCommunication: (id, data) => api.post(`/leads/${id}/communication`, data),
  convert: (id, data) => api.post(`/leads/${id}/convert`, data),
  close: (id, data) => api.post(`/leads/${id}/close`, data),
};

// Client APIs
export const clientAPI = {
  getAll: (params) => api.get('/clients', { params }),
  getOne: (id) => api.get(`/clients/${id}`),
  create: (data) => api.post('/clients', data),
  update: (id, data) => api.put(`/clients/${id}`, data),
  approve: (id) => api.post(`/clients/${id}/approve`),
  assignStaff: (id, staffIds) => api.post(`/clients/${id}/assign-staff`, staffIds),
  delete: (id) => api.delete(`/clients/${id}`),
  resetPassword: (id) => api.post(`/clients/${id}/reset-password`),
  getAccreditationStandards: () => api.get('/accreditation-standards/active'),
  setValidity: (id, data) => api.put(`/clients/${id}/accreditation-validity`, data),
  getApplications: (id) => api.get(`/clients/${id}/applications`),
  renew: (id, data) => api.post(`/clients/${id}/renew`, data),
};

// Accreditation Standards CRUD
export const standardAPI = {
  getAll: () => api.get('/accreditation-standards'),
  getActive: () => api.get('/accreditation-standards/active'),
  getOne: (id) => api.get(`/accreditation-standards/${id}`),
  create: (data) => api.post('/accreditation-standards', data),
  update: (id, data) => api.put(`/accreditation-standards/${id}`, data),
  delete: (id) => api.delete(`/accreditation-standards/${id}`),
};

// RBAC Configuration
export const rbacAPI = {
  getMenuItems: () => api.get('/rbac/menu-items'),
  getConfig: () => api.get('/rbac/config'),
  updateConfig: (data) => api.put('/rbac/config', data),
  getMyMenu: () => api.get('/rbac/my-menu'),
};

// Phase APIs
export const phaseAPI = {
  getAll: (params) => api.get('/phases', { params }),
  getOne: (id) => api.get(`/phases/${id}`),
  update: (id, data) => api.put(`/phases/${id}`, data),
  getOverview: () => api.get('/phases/overview/all-clients'),
  getMetadata: () => api.get('/phase-metadata'),
  getConfig: (clientId) => api.get(`/phase-config/${clientId}`),
  updateConfig: (clientId, data) => api.put(`/phase-config/${clientId}`, data),
  regenerateChecklist: (phaseId) => api.post(`/phases/${phaseId}/regenerate-checklist`),
  regenerateTasks: (phaseId) => api.post(`/phases/${phaseId}/regenerate-tasks`),
  verify: (phaseId, data) => api.post(`/phases/${phaseId}/verify`, data),
};

// Task APIs
export const taskAPI = {
  getAll: (params) => api.get('/tasks', { params }),
  getOne: (id) => api.get(`/tasks/${id}`),
  create: (data) => api.post('/tasks', data),
  update: (id, data) => api.put(`/tasks/${id}`, data),
  delete: (id) => api.delete(`/tasks/${id}`),
  bulkDelete: (ids) => api.post('/tasks/bulk-delete', { task_ids: ids }),
  complete: (id, data) => api.post(`/tasks/${id}/complete`, data),
  verify: (id, data) => api.post(`/tasks/${id}/verify`, data),
  getEvidence: (id) => api.get(`/tasks/${id}/evidence`),
  uploadEvidence: (id, data) => api.post(`/tasks/${id}/evidence`, data),
  getAssignableUsers: (clientId) => api.get(`/tasks/assignable-users/${clientId}`),
  submitSummary: (id, data) => api.post(`/tasks/${id}/summary`, data),
  approveSummary: (id, data) => api.post(`/tasks/${id}/summary/action`, data),
  downloadSummary: (id) => api.get(`/tasks/${id}/summary/download`, { responseType: 'blob' }),
  sendSummaryToClient: (id) => api.post(`/tasks/${id}/send-summary-to-client`),
  getEvidence: (id) => api.get(`/tasks/${id}/evidence`),
  downloadEvidence: (evId) => api.get(`/tasks/evidence/${evId}/download`, { responseType: 'blob' }),
  downloadIcs: (id) => api.get(`/tasks/${id}/ics`, { responseType: 'blob' }),
  deleteEvidence: (evId) => api.delete(`/tasks/evidence/${evId}`),
  adminEditSummary: (id, data) => api.put(`/tasks/${id}/edit-summary`, data),
  adminUploadEvidence: (id, data) => api.post(`/tasks/${id}/admin-upload-evidence`, data),
};

// Roster APIs
export const rosterAPI = {
  getAll: (params) => api.get('/roster', { params }),
  create: (data) => api.post('/roster', data),
  bulkCreate: (data) => api.post('/roster/bulk-create', data),
  update: (id, data) => api.put(`/roster/${id}`, data),
  delete: (id) => api.delete(`/roster/${id}`),
  checkConflicts: (consultantId, date) => api.get('/roster/conflicts', { params: { consultant_id: consultantId, date } }),
  getMonthlySummary: (month) => api.get('/roster/monthly-summary', { params: { month } }),
  getClientSummary: (month, clientId) => api.get('/roster/client-summary', { params: { month, client_id: clientId } }),
};

// Leave APIs
export const leaveAPI = {
  getAll: (params) => api.get('/leaves', { params }),
  getOne: (id) => api.get(`/leaves/${id}`),
  create: (data) => api.post('/leaves', data),
  update: (id, data) => api.put(`/leaves/${id}`, data),
  action: (id, data) => api.post(`/leaves/${id}/action`, data),
  cancel: (id) => api.post(`/leaves/${id}/cancel`),
  checkConflicts: (staffId, date) => api.get('/leaves/check-conflicts', { params: { staff_id: staffId, date } }),
  getStats: () => api.get('/leaves/stats'),
  getBalance: (staffId) => api.get('/leaves/balance', { params: staffId ? { staff_id: staffId } : {} }),
  getCalendarSummary: (year) => api.get('/leaves/calendar-summary', { params: year ? { year } : {} }),
  updateLeaveLimit: (userId, maxLeaveLimit) => api.put(`/users/${userId}/leave-limit`, { max_leave_limit: maxLeaveLimit }),
  assignAdjustSunday: (id, adjustSundayDate) => api.put(`/leaves/${id}/assign-adjust-sunday`, { adjust_sunday_date: adjustSundayDate }),
};

// Visit APIs
export const visitAPI = {
  getAll: (params) => api.get('/visits', { params }),
  getOne: (id) => api.get(`/visits/${id}`),
  create: (data) => api.post('/visits', data),
  bulkCreate: (data) => api.post('/visits/bulk-create', data),
  update: (id, data) => api.put(`/visits/${id}`, data),
  checkLeaveConflicts: (date) => api.get('/visits/check-leave-conflicts', { params: { date } }),
  checkVisitConflicts: (data) => api.post('/visits/check-conflicts', data),
  downloadIcs: (id) => api.get(`/visits/${id}/ics`, { responseType: 'blob' }),
  submitSummary: (id, data) => api.post(`/visits/${id}/summary`, data),
  approveSummary: (id, data) => api.post(`/visits/${id}/summary/action`, data),
  downloadSummary: (id) => api.get(`/visits/${id}/summary/download`, { responseType: 'blob' }),
  sendSummaryToClient: (id) => api.post(`/visits/${id}/send-summary-to-client`),
  adminEdit: (id, data) => api.put(`/visits/${id}/admin-edit`, data),
};

// Document APIs
export const documentAPI = {
  getAll: (params) => api.get('/documents', { params }),
  getOne: (id) => api.get(`/documents/${id}`),
  upload: (data) => api.post('/documents', data),
  delete: (id) => api.delete(`/documents/${id}`),
};

// Ticket APIs
export const ticketAPI = {
  getAll: (params) => api.get('/tickets', { params }),
  getOne: (id) => api.get(`/tickets/${id}`),
  create: (data) => api.post('/tickets', data),
  update: (id, data) => api.put(`/tickets/${id}`, data),
  addMessage: (id, data) => api.post(`/tickets/${id}/message`, data),
};

// Invoice APIs
export const invoiceAPI = {
  getAll: (params) => api.get('/invoices', { params }),
  getOne: (id) => api.get(`/invoices/${id}`),
  create: (data) => api.post('/invoices', data),
  markPaid: (id) => api.put(`/invoices/${id}/mark-paid`),
};

// Notification APIs
export const notificationAPI = {
  getAll: (params) => api.get('/notifications', { params }),
  markRead: (id) => api.put(`/notifications/${id}/read`),
  markAllRead: () => api.put('/notifications/mark-all-read'),
  getSidebarCounts: () => api.get('/sidebar-counts'),
};

// Personal Calendar Subscription
export const myCalendarAPI = {
  getToken: () => api.get('/me/calendar-token'),
  rotateToken: () => api.post('/me/calendar-token'),
  revokeToken: () => api.delete('/me/calendar-token'),
};

// Reports (consolidated visit + task summaries)
export const reportsAPI = {
  list: (params) => api.get('/reports/summaries', { params }),
  downloadAll: (params) => api.get('/reports/summaries/download', { params, responseType: 'blob' }),
};

// Sticky Notes APIs
export const stickyNoteAPI = {
  getAll: (params) => api.get('/sticky-notes', { params }),
  create: (data) => api.post('/sticky-notes', data),
  complete: (id) => api.put(`/sticky-notes/${id}/complete`),
  reopen: (id) => api.put(`/sticky-notes/${id}/reopen`),
  delete: (id) => api.delete(`/sticky-notes/${id}`),
};

// NC APIs
export const ncAPI = {
  getAll: (params) => api.get('/ncs', { params }),
  create: (data) => api.post('/ncs', data),
  update: (id, data) => api.put(`/ncs/${id}`, data),
};

// Checklist APIs
export const checklistAPI = {
  getAll: (params) => api.get('/checklists', { params }),
  create: (data) => api.post('/checklists', data),
  update: (id, data) => api.put(`/checklists/${id}`, data),
  delete: (id) => api.delete(`/checklists/${id}`),
  generateForPhase: (phaseId) => api.post(`/phases/${phaseId}/generate-checklist`),
};

// Communications APIs
export const commsAPI = {
  getStats: () => api.get('/communications/stats'),
  getTemplates: (params) => api.get('/communications/templates', { params }),
  createTemplate: (data) => api.post('/communications/templates', data),
  updateTemplate: (id, data) => api.put(`/communications/templates/${id}`, data),
  deleteTemplate: (id) => api.delete(`/communications/templates/${id}`),
  getLogs: (params) => api.get('/communications/logs', { params }),
  sendMessage: (data) => api.post('/communications/send', data),
  seedTemplates: () => api.post('/communications/seed-templates'),
  getConfig: () => api.get('/communications/config'),
  updateEmailConfig: (data) => api.put('/communications/config/email', data),
  updateWhatsappConfig: (data) => api.put('/communications/config/whatsapp', data),
  testEmail: (data) => api.post('/communications/config/test-email', data),
  testWhatsapp: (data) => api.post('/communications/config/test-whatsapp', data),
  getBankDetails: () => api.get('/communications/config/bank'),
  updateBankDetails: (data) => api.put('/communications/config/bank', data),
  updateGmailConfig: (data) => api.put('/communications/config/gmail', data),
  testGmail: (data) => api.post('/communications/config/test-gmail', data),
};

// Location APIs
export const locationAPI = {
  getAll: (params) => api.get('/locations', { params }),
  create: (data) => api.post('/locations', data),
  update: (id, data) => api.put(`/locations/${id}`, data),
  delete: (id) => api.delete(`/locations/${id}`),
};

// Audit Trail APIs
export const auditAPI = {
  getAll: (params) => api.get('/audit-trail', { params }),
  getStats: () => api.get('/audit-trail/stats'),
};

// Export APIs (return blob for download)
export const exportAPI = {
  clients: () => api.get('/export/clients', { responseType: 'blob' }),
  phases: (params) => api.get('/export/phases', { params, responseType: 'blob' }),
  tasks: () => api.get('/export/tasks', { responseType: 'blob' }),
  invoices: () => api.get('/export/invoices', { responseType: 'blob' }),
  auditTrail: () => api.get('/export/audit-trail', { responseType: 'blob' }),
};

// Notification APIs
export const realtimeAPI = {
  getNotifications: () => api.get('/notifications/realtime'),
  markRead: (id) => api.put(`/notifications/realtime/${id}/read`),
};

// Scheduled Reminders APIs
export const reminderAPI = {
  getAll: () => api.get('/reminders'),
  create: (data) => api.post('/reminders', data),
  update: (id, data) => api.put(`/reminders/${id}`, data),
  delete: (id) => api.delete(`/reminders/${id}`),
  trigger: (id) => api.post(`/reminders/${id}/trigger`),
  seedDefaults: () => api.post('/reminders/seed-defaults'),
};

// Analytics API
export const analyticsAPI = {
  getOverview: () => api.get('/analytics/overview'),
  getReadiness: () => api.get('/analytics/readiness'),
};

// Client Self-Service API
export const selfServiceAPI = {
  getProgress: () => api.get('/self-service/progress'),
  submitEvidence: (itemId, notes) => api.post(`/self-service/checklist/${itemId}/evidence`, null, { params: { notes } }),
  submitNCResponse: (ncId, responseText) => api.post(`/self-service/nc/${ncId}/response`, null, { params: { response_text: responseText } }),
};

// Dashboard APIs
export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
  getClientDashboard: (clientId) => api.get(`/dashboard/client/${clientId}`),
};

// Seed API
export const seedAPI = {
  seed: () => api.post('/seed'),
};

// AI Generation APIs
export const aiAPI = {
  generateGapReport: (data) => api.post('/ai/generate-gap-report', data),
  generateSOP: (data) => api.post('/ai/generate-sop', data),
  generateTrainingModule: (data) => api.post('/ai/generate-training-module', data),
};

// PDF APIs
export const pdfAPI = {
  getInvoicePdf: (invoiceId) => api.get(`/invoices/${invoiceId}/pdf`, { responseType: 'blob' }),
  getVisitReportPdf: (visitId) => api.get(`/visits/${visitId}/report-pdf`, { responseType: 'blob' }),
};

// Notification Actions APIs
export const notificationActionsAPI = {
  sendInvoiceEmail: (invoiceId) => api.post(`/invoices/${invoiceId}/send-email`),
  sendVisitReminder: (visitId) => api.post(`/visits/${visitId}/send-reminder`),
  sendPhaseUpdate: (clientId, phaseId) => api.post(`/notifications/send-phase-update?client_id=${clientId}&phase_id=${phaseId}`),
};

// Attendance API
export const attendanceAPI = {
  getAll: (params) => api.get('/attendance', { params }),
  mark: (data) => api.post('/attendance/mark', data),
  bulkMark: (data) => api.post('/attendance/bulk-mark', data),
  getSummary: (month) => api.get('/attendance/summary', { params: { month } }),
  getMy: (month) => api.get('/attendance/my', { params: { month } }),
};

// Message Templates API
export const messageTemplateAPI = {
  getAll: () => api.get('/message-templates'),
  update: (id, data) => api.put(`/message-templates/${id}`, data),
};

// Messaging API
export const messagingAPI = {
  send: (data) => api.post('/messages/send', data),
  getLogs: (limit) => api.get('/messages/logs', { params: { limit } }),
};

// Integration Status API
export const integrationAPI = {
  getStatus: () => api.get('/integrations/status'),
};

export default api;
